import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { asset } from "@/lib/api";
import { IDLE, useAudioEngine, useYouTubeEngine } from "./engines";

const kindOf = (t) => (t?.video_id ? "yt" : "audio");
const srcOf = (t) => t.video_id || (t.source === "file" ? asset(t.url) : t.preview_url);
export const isPreview = (t) => t?.source === "spotify" && !t?.video_id;
export const isPlayable = (t) => !!(t?.video_id || t?.url || t?.preview_url);

const storedVolume = () => {
  const v = Number(localStorage.getItem("emporio-volume"));
  return localStorage.getItem("emporio-volume") !== null && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.8;
};

export function usePlayer(tracks, sectionTracks) {
  const [idx, setIdx] = useState(0);
  const [cur, setCur] = useState(null);
  const [section, setSection] = useState(null);
  const [kind, setKind] = useState(null);
  const [volume, setVolume] = useState(storedVolume);
  const [muted, setMuted] = useState(() => localStorage.getItem("emporio-muted") === "1");
  const ended = useRef(() => {});
  const failed = useRef(() => {});
  const fails = useRef(0);
  const [fade, setFade] = useState(1);
  const fadeRef = useRef(1);
  const fadeTimer = useRef(null);
  const level = volume * fade;
  const audio = useAudioEngine(ended, level, muted);
  const yt = useYouTubeEngine({
    onEnded: ended, onError: failed, active: kind === "yt", volume: level, muted,
    enabled: tracks.some((t) => t.video_id) || Object.values(sectionTracks).some((t) => t?.video_id),
  });
  const engines = { audio, yt };

  useEffect(() => {
    localStorage.setItem("emporio-volume", String(volume));
    localStorage.setItem("emporio-muted", muted ? "1" : "0");
  }, [volume, muted]);

  useEffect(() => () => clearInterval(fadeTimer.current), []);

  // Ramp the fade multiplier (0..1) over `ms`, then run `done`.
  const ramp = (to, ms, done) => {
    clearInterval(fadeTimer.current);
    const from = fadeRef.current;
    const t0 = performance.now();
    fadeTimer.current = setInterval(() => {
      const k = Math.min(1, (performance.now() - t0) / ms);
      fadeRef.current = from + (to - from) * k;
      setFade(fadeRef.current);
      if (k >= 1) { clearInterval(fadeTimer.current); if (done) done(); }
    }, 40);
  };
  // Fade the current song out, switch, then fade the next one in.
  const crossfade = (switchFn) => ramp(0, 700, () => { switchFn(); ramp(1, 1200); });
  const instant = () => { clearInterval(fadeTimer.current); fadeRef.current = 1; setFade(1); };

  const start = (t) => {
    const k = kindOf(t);
    Object.entries(engines).forEach(([name, e]) => { if (name !== k) e.pause(); });
    setCur(t);
    setKind(k);
    engines[k].load(srcOf(t));
  };
  const play = (i) => {
    const t = tracks[i];
    if (!t) return;
    setIdx(i);
    setSection(null);
    start(t);
  };
  const playSection = (name) => {
    const t = sectionTracks[name];
    if (!t || !isPlayable(t)) return;
    setSection(name);
    start(t);
  };
  const step = (d) => { instant(); play((idx + d + tracks.length) % tracks.length); };

  ended.current = () => { fails.current = 0; if (section && cur) start(cur); else step(1); };
  failed.current = () => {
    fails.current += 1;
    if (section) { toast.error("This section's song can't play here — back to the playlist."); play(idx); return; }
    if (fails.current < tracks.length) step(1);
  };

  const pb = kind ? engines[kind].pb : IDLE;
  return {
    ytHost: yt.host,
    idx,
    cur: cur || tracks[idx],
    section,
    started: kind !== null,
    playing: kind !== null && !pb.isPaused,
    pb,
    volume,
    muted,
    setVolume: (v) => { setVolume(v); if (v > 0) setMuted(false); },
    toggleMute: () => setMuted((m) => !m),
    seek: (ms) => { if (kind) engines[kind].seek(ms); },
    play: (i) => { instant(); play(i); },
    playSection: (name) => { instant(); playSection(name); },
    fadeToSection: (name) => crossfade(() => playSection(name)),
    fadeToQueue: () => crossfade(() => play(idx)),
    next: () => step(1),
    prev: () => step(-1),
    toggle: () => (kind ? engines[kind].toggle() : play(idx)),
  };
}
